import {addDoc, collection} from 'firebase/firestore';
import {useState} from 'react';
import {env} from '../../../constants';
import {firebaseAuth, firebaseFirestore} from '../../../setup/firebase';
import {extractSheetIdFromUrl} from '../../../hook/generationJob/extractSheetIdFromUrl';
import {
  etsyBulkUpdateGoogleSheetLinkFirebaseSchema,
  etsyBulkUpdateGoogleSheetLinkInputSchema,
  type EtsyBulkUpdateGoogleSheetLink,
  type EtsyBulkUpdateGoogleSheetLinkInput,
} from './schema';

export interface UseCreateEtsyBulkUpdateGoogleSheetLinkOptions {
  onCompleted?: (data: EtsyBulkUpdateGoogleSheetLink) => void;
  onError?: (error: Error) => void;
}

export default function useCreateEtsyBulkUpdateGoogleSheetLink(
  options?: UseCreateEtsyBulkUpdateGoogleSheetLinkOptions,
) {
  const [loading, setLoading] = useState(false);

  const createLink = async (input: EtsyBulkUpdateGoogleSheetLinkInput) => {
    const user = firebaseAuth.currentUser;
    if (!user) {
      const err = new Error('Not authenticated');
      options?.onError?.(err);
      return;
    }
    setLoading(true);
    try {
      const parsed = etsyBulkUpdateGoogleSheetLinkInputSchema.parse(input);
      const spreadsheetId = extractSheetIdFromUrl(parsed.spreadsheetUrlOrId);
      const data = {
        ...(parsed.name != null && parsed.name !== '' && {name: parsed.name}),
        etsyAccountId: parsed.etsyAccountId,
        spreadsheetId,
        listingState: parsed.listingState,
        gdriveTokenId: parsed.gdriveTokenId,
        creationTimestamp: Math.floor(Date.now() / 1000),
      };
      etsyBulkUpdateGoogleSheetLinkFirebaseSchema.parse(data);
      const collRef = collection(firebaseFirestore, 'user', user.uid, env.collection.etsyBulkUpdateGoogleSheetLink);
      const docRef = await addDoc(collRef, data);
      const result: EtsyBulkUpdateGoogleSheetLink = {
        id: docRef.id,
        name: parsed.name ?? undefined,
        etsyAccountId: parsed.etsyAccountId,
        spreadsheetId,
        listingState: parsed.listingState,
        gdriveTokenId: parsed.gdriveTokenId,
        creationTimestamp: data.creationTimestamp,
      };
      options?.onCompleted?.(result);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      options?.onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return [createLink, {loading}] as const;
}
